import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {DatePipe, InputDropdownCheckboxesComponent, Sorting} from '@brunata-angular/ui';
import {TRANSLATE, TranslatePipe} from '@brunata-angular/util';
import {SavedExport} from '../saved-export.model';
import {SavedExportFilterForm} from '../saved-exports-filters.model';
import {FileFormatBadgeComponent} from '../../shared/ui/file-format-badge/file-format-badge.component';
import {
    ExportTableColumn,
    ExportTableComponent,
} from '../../shared/ui/export-table/export-table.component';
import {ExportRowActionsComponent} from '../../shared/ui/export-row-actions/export-row-actions.component';
import {DeliveryStatusComponent} from '../delivery-status/delivery-status.component';
import {ExportNowButtonComponent} from '../export-now-button/export-now-button.component';

@Component({
    selector: 'app-saved-exports-table',
    standalone: true,
    imports: [
        DatePipe,
        ReactiveFormsModule,
        TranslatePipe,
        FileFormatBadgeComponent,
        InputDropdownCheckboxesComponent,
        ExportRowActionsComponent,
        ExportTableComponent,
        DeliveryStatusComponent,
        ExportNowButtonComponent,
    ],
    template: `
        <app-export-table
            [columns]="columns()"
            [currentSorting]="currentSorting()"
            [responsive]="alignedDesign()"
            (sort)="sort.emit($event)">
            <tr appExportTableFilters class="bg-light" [formGroup]="filterForm()">
                <td class="align-top">
                    <app-input-dropdown-checkboxes
                        formControlName="outputFormats"
                        [placeholder]="
                            alignedDesign()
                                ? ('savedexport_overview_filter_format_all'
                                  | translate: 'online' : 'export')
                                : ''
                        " />
                </td>
                <td class="align-top">
                    <input
                        type="search"
                        class="form-control"
                        formControlName="searchTerm"
                        [placeholder]="
                            'savedexport_overview_search_placeholder'
                                | translate: 'online' : 'export'
                        " />
                </td>
                <td class="align-top">
                    <app-input-dropdown-checkboxes
                        formControlName="schedules"
                        [placeholder]="
                            alignedDesign()
                                ? ('savedexport_overview_filter_schedule_all'
                                  | translate: 'online' : 'export')
                                : ''
                        " />
                </td>
                <td class="align-top"></td>
                @if (deliveryLog()) {
                    <td class="align-top"></td>
                }
                <td class="align-top text-end">
                    <button
                        type="button"
                        class="btn btn-outline-primary d-inline-flex align-items-center gap-2 text-nowrap"
                        (click)="filtersReset.emit()">
                        <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                        {{ 'savedexport_overview_filter_reset' | translate: 'online' : 'export' }}
                    </button>
                </td>
            </tr>
            @for (item of savedExports(); track item.savedExportId) {
                <tr [class.text-nowrap]="oneLineNames()">
                    <td class="align-middle">
                        @if (item.outputFormat != null) {
                            <app-file-format-badge [format]="item.outputFormat" />
                        }
                    </td>
                    <td
                        class="align-middle fw-bold"
                        [class.truncated-cell]="oneLineNames()"
                        [class.w-100]="oneLineNames()">
                        <div
                            [class.text-truncate]="oneLineNames()"
                            [attr.title]="oneLineNames() ? item.name : null">
                            {{ item.name }}
                        </div>
                    </td>
                    <td class="align-middle">
                        {{ item.scheduleLabelKey | translate: 'online' : 'export' }}
                    </td>
                    <td class="align-middle">
                        {{ item.startDate != null ? (item.startDate | appdate) : '-' }}
                    </td>
                    @if (deliveryLog()) {
                        <td class="align-middle">
                            <app-delivery-status
                                [savedExport]="item"
                                (viewLog)="viewLog.emit(item)" />
                        </td>
                    }
                    <td class="align-middle text-end">
                        <div class="d-inline-flex align-items-center gap-2">
                            <app-export-now-button
                                [exporting]="exportingIds().has(item.savedExportId)"
                                [deliveryRequested]="deliveryRequestedIds().has(item.savedExportId)"
                                (run)="export.emit(item)" />
                            <app-export-row-actions
                                [showDuplicate]="true"
                                (edit)="edit.emit(item)"
                                (duplicate)="duplicate.emit(item)"
                                (delete)="delete.emit(item)" />
                        </div>
                    </td>
                </tr>
            } @empty {
                <tr>
                    <td [attr.colspan]="columns().length">
                        {{ 'savedexport_overview_no_matches' | translate: 'online' : 'export' }}
                    </td>
                </tr>
            }
        </app-export-table>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedExportsTableComponent {
    readonly savedExports = input.required<SavedExport[]>();
    readonly currentSorting = input.required<Sorting>();
    readonly filterForm = input.required<SavedExportFilterForm>();
    readonly deliveryLog = input(false);
    readonly alignedDesign = input(false);
    readonly oneLineNames = input(false);
    readonly exportingIds = input<ReadonlySet<string>>(new Set());
    readonly deliveryRequestedIds = input<ReadonlySet<string>>(new Set());
    readonly sort = output<Sorting>();
    readonly filtersReset = output<void>();
    readonly export = output<SavedExport>();
    readonly edit = output<SavedExport>();
    readonly duplicate = output<SavedExport>();
    readonly delete = output<SavedExport>();
    readonly viewLog = output<SavedExport>();

    private readonly translate = inject(TRANSLATE);

    protected readonly columns = computed<ExportTableColumn[]>(() => [
        {label: this.translate('savedexport_savedexports_column_format', 'online', 'export')},
        {
            label: this.translate('savedexport_savedexports_column_name', 'online', 'export'),
            sortColumn: 'name',
        },
        {label: this.translate('savedexport_savedexports_column_schedule', 'online', 'export')},
        {label: this.translate('reporting_START_DATE'), sortColumn: 'startDate'},
        ...(this.deliveryLog() ? [this.statusColumn()] : []),
        {
            label: this.translate('savedexport_savedexports_column_actions', 'online', 'export'),
            alignEnd: true,
        },
    ]);

    private statusColumn(): ExportTableColumn {
        return {
            label: this.translate('savedexport_savedexports_column_status', 'online', 'export'),
        };
    }
}
